import { useCallback } from 'react';

import { isEntertainment } from '../../shared/helpers/assetHelpers';
import { getSubscriptionUrl } from '../SubscribeButton/getSubscriptionUrl';
import { trackSubscribeClickEvent } from '../../services/tracking/pulseEvents';
import { config } from '../../config';
import { Button } from '../Button/Button';
import { useSessionInvalidation } from '../utils/useSessionInvalidation';
import { getCurrentPageViewData } from '../../services/tracking/helpers/pulseOrigin';
import { isSportAsset } from '../../player/playerAssetHelpers';

import type { ButtonProps } from '../Button/Button';
import type { ProviderSubscriptionMap } from '../../services/tracking/pulseEvents';
import type { Provider } from '@vgtv/api-client';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';
import type { FC } from 'react';

interface SimpleSubscribeButtonProps {
  asset: SvpAssetAdditional;
  label: string;
  price?: number;
  access?: ProviderAccessMap[Provider];
  size: ButtonProps['size'];
}

const SimpleSubscribeButton: FC<SimpleSubscribeButtonProps> = ({
  asset,
  label: labelProp,
  price: priceProp,
  access,
  size = 'large',
}) => {
  let label = labelProp;
  let price = priceProp;
  let codeOverride: string | undefined;

  if (config.provider === 'ab') {
    codeOverride = 'AB-2M129-E-VIDEO-149';
    label = `Skaffa Plus 2 mån ${price}kr`;

    if (isEntertainment(asset)) {
      codeOverride = 'AB-3M19-50P-E-VIDEO-149';
      price = 19;
      label = 'Prova Plus för 19 kr';
    } else if (isSportAsset(asset)) {
      codeOverride = 'AB-2M149-E-VIDEO-149';
      price = 149;
      label = `${price} kr för 2 mån`;
    }
  }

  const subscriptionUrl = getSubscriptionUrl(asset, {
    access,
    code: codeOverride,
  });

  useSessionInvalidation();

  const handleSubscribeClick = useCallback(
    async (e) => {
      e.preventDefault();

      try {
        await trackSubscribeClickEvent({
          button: {
            elementType: 'SubscribeButton',
            title: label,
          },
          subscription: {
            url: subscriptionUrl.split('?')[0],
            price,
            provider: config.provider,
            name: access as ProviderSubscriptionMap[typeof config.provider],
          },
          xDomainId: getCurrentPageViewData().xDomainId,
        });
      } catch {}

      window.location.href = e.target.href;
    },
    [subscriptionUrl, price, label, access]
  );

  return (
    <Button
      size={size}
      component="a"
      variant="contained"
      rounded
      isActiveBrandColor
      href={subscriptionUrl}
      onClick={handleSubscribeClick}
    >
      {label}
    </Button>
  );
};

export { SimpleSubscribeButton };
